import React, { useMemo } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import PropTypes from 'prop-types';
import block from 'bem-cn';

import SVG from 'components/SVG';
import Button from 'components/Button';
import Dropdown from 'components/Dropdown';
import { getActionItems, userTypes } from '../../../data';

import iconSVG from './img/icon.svg';

import './TableRow.scss';
import { useDispatch } from 'react-redux';
import { actions as reportsActions } from 'features/reports/redux';
import { useState } from 'react';
import { useCallback } from 'react';
import Modal from 'components/ModalProvider/Modal/Modal';
import CashbackActivate from '../Modals/CashbackActivate/CashbackActivate';
import CashbackTerms from '../Modals/CashbackTerms/CashbackTerms';
import { actions as usersActions } from 'features/users/redux';

const b = block('adm-bets-table-row-user');

const TableRow = ({ item, isWithdrawalAccess, isDepositAccess, locale, dropdownCallback }) => {
  const { deposit_level_settings } = useSelector(state => state.userSettings, shallowEqual);
  const currency = useSelector(state => state.auth.currency, shallowEqual);

  const { isCashbackActive } = useSelector(state => state.auth, shallowEqual);
  const dispatch = useDispatch();

  const [isCashbackActivateModalOpen, setIsCashbackActivateModalOpen] = useState(false);
  const [isCashbackTermsModalOpen, setIsCashbackTermsModalOpen] = useState(false);

  const activateCashbackForOneUser = useCallback(
    (callback, isSwitcherActive) => {
      if (isSwitcherActive) {
        setIsCashbackActivateModalOpen(true);
      } else {
        dispatch(
          reportsActions.activateCashbackForOneUser(
            {
              activate: isSwitcherActive, // Используем переданное значение
              id: item.id,
            },
            callback,
          ),
        );
      }
    },
    [dispatch, item.id],
  );

  const openCashbackActivateModal = useCallback((status, callback) => {
    setIsCashbackActivateModalOpen(status);

    if (callback) {
      callback();
    }
  }, []);

  const openCashbackTermsModal = useCallback(status => {
    setIsCashbackTermsModalOpen(status);
  }, []);

  const handleDropDownCallback = useCallback(
    (callback, isSwitcherActive) => {
      dispatch(
        reportsActions.activateCashbackForOneUser(
          {
            activate: isSwitcherActive, // Используем переданное значение
            id: item.id,
          },
          () => {
            callback();
            dropdownCallback();
          },
        ),
      );
    },
    [dropdownCallback],
  );

  const icon = useMemo(() => {
    if (item.totalDeposits) {
      if (
        item.totalDeposits >= deposit_level_settings.black.from &&
        item.totalDeposits <= deposit_level_settings.black.to
      )
        return (
          <div className={b('td-data-icon-wrapper')}>
            <SVG svgProps={{ svg: iconSVG }} className={b('td-data-icon', { color: 'black' })} />
            <div className={b('td-data-icon-text')}>{locale.black}</div>
          </div>
        );

      if (
        item.totalDeposits >= deposit_level_settings.gold.from &&
        item.totalDeposits <= deposit_level_settings.gold.to
      )
        return (
          <div className={b('td-data-icon-wrapper')}>
            <SVG svgProps={{ svg: iconSVG }} className={b('td-data-icon', { color: 'gold' })} />
            <div className={b('td-data-icon-text')}>{locale.gold}</div>
          </div>
        );

      if (
        item.totalDeposits >= deposit_level_settings.silver.from &&
        item.totalDeposits <= deposit_level_settings.silver.to
      )
        return (
          <div className={b('td-data-icon-wrapper')}>
            <SVG svgProps={{ svg: iconSVG }} className={b('td-data-icon', { color: 'silver' })} />
            <div className={b('td-data-icon-text')}>{locale.silver}</div>
          </div>
        );

      if (
        item.totalDeposits >= deposit_level_settings.bronze.from &&
        item.totalDeposits <= deposit_level_settings.bronze.to
      )
        return (
          <div className={b('td-data-icon-wrapper')}>
            <SVG svgProps={{ svg: iconSVG }} className={b('td-data-icon', { color: 'bronze' })} />
            <div className={b('td-data-icon-text')}>{locale.bronze}</div>
          </div>
        );
    }

    return (
      <div className={b('td-data-icon-wrapper')}>
        <SVG svgProps={{ svg: userTypes[item?.role]?.icon }} className={b('td-data-icon')} />
      </div>
    );
  }, [item, deposit_level_settings, locale]);

  return (
    <div className={b({ banned: item.isBanned })}>
      {isCashbackActivateModalOpen && (
        <Modal
          closeFunction={() => openCashbackActivateModal(false)}
          content={
            <CashbackActivate
              closeFunction={() => openCashbackActivateModal(false)}
              callback={handleDropDownCallback}
            />
          }
        />
      )}
      {isCashbackTermsModalOpen && (
        <Modal
          closeFunction={() => openCashbackTermsModal(false)}
          content={<CashbackTerms closeFunction={() => openCashbackTermsModal(false)} />}
        />
      )}
      <div className={b('td')}>
        <div className={b('td-data')}>
          {icon}
          <div className={b('td-data-user')}>
            <span>{item.username}</span>
            <div className={b('td-data-user-type', { role: item.role })}>{userTypes[item.role].type}</div>
          </div>
        </div>
      </div>
      <div className={b('td')}>
        {item.balance} {currency}
      </div>
      <div className={b('td')}>
        <div className={b('td-buttons')}>
          {isDepositAccess && <Button disabled={item.isBanned} link={`/user/deposit/${item.id}`} text={locale.topup} />}
          {isWithdrawalAccess && (
            <Button
              disabled={item.isBanned}
              link={`/user/withdrawal/${item.id}`}
              color="full-transparent"
              text={locale.withdrawal}
            />
          )}
        </div>
      </div>
      <div className={b('td')}>{item.creationDate}</div>
      <div className={b('td')}>{item.commissionPercent && `${item.commissionPercent}%`}</div>
      <div className={b('td')}>
        {+item.role !== 3 && (
          <div className={b('td-actions')}>
            <Dropdown
              user={item}
              items={getActionItems({ role: item.role, isBanned: item.isBanned, item, isCashbackActive })}
              locale={locale}
              switcherCallback={activateCashbackForOneUser}
              switcherActive={item.isCashbackActive}
              termsCallback={() => openCashbackTermsModal(true)}
              dropdownCallback={dropdownCallback}
            />
          </div>
        )}
      </div>
    </div>
  );
};

TableRow.propTypes = {
  item: PropTypes.object.isRequired,
};

export default TableRow;
